// Angular
import { Directive, ElementRef, Renderer2, AfterViewInit, OnChanges, SimpleChanges, OnDestroy, input, inject } from '@angular/core';
import { AbstractControl, NgControl } from '@angular/forms';
// RxJS
import { Subscription } from 'rxjs';

@Directive({
    selector: '[highlightBorder]'
})
export class HighlightBorderDirective implements AfterViewInit, OnChanges, OnDestroy {

    // Private properties
    private el = inject(ElementRef);
    private renderer = inject(Renderer2);
    private control = inject(NgControl, { optional: true, self: true });
    private matFormField!: HTMLElement | null;
    private static cache = new WeakMap<Element, HTMLElement | null>();
    private valueChangeSub!: Subscription;

    // Inputs
    readonly highlightBorder = input<boolean>(false);

    /**
     * After view init lifecycle hook
     * @returns {void}
     * @memberof HighlightBorderDirective
     */
    ngAfterViewInit(): void {
        if (!this.control?.control) {
            console.warn('⚠️ NgControl is missing! Ensure this directive is applied to an input inside a form field.');
            return;
        }
        if (!this.highlightBorder()) { return; }

        this.matFormField = HighlightBorderDirective.cache.get(this.el.nativeElement)
            || this.findClosestMatFormField();

        if (this.matFormField) {
            HighlightBorderDirective.cache.set(this.el.nativeElement, this.matFormField);
            this.applyBorder(); // Apply border initially

            // Subscribe to valueChanges and store the subscription
            this.valueChangeSub = this.control.control.valueChanges?.subscribe(() => this.applyBorder());
        } else {
            console.warn('⚠️ Could not find mat-mdc-form-field parent!', this.el.nativeElement);
        }
    }

    /**
     * Detect changes in the `highlightBorder` input
     * @param {SimpleChanges} changes
     * @memberof HighlightBorderDirective
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['highlightBorder']) {
            this.applyBorder(); // Apply border on `isUpdateFlow` changes
        }
    }

    /**
     * Clean up subscriptions to prevent memory leaks
     */
    ngOnDestroy(): void {
        if (this.valueChangeSub) {
            this.valueChangeSub.unsubscribe();
        }
        if (this.matFormField) {
            HighlightBorderDirective.cache.delete(this.el.nativeElement); // ✅ Manually clear from cache
        }
    }

    /**
     * Apply border based on the control's value and required state
     * @private
     * @returns {void}
     * @memberof HighlightBorderDirective
     */
    private applyBorder(): void {
        if (!this.control?.control || !this.matFormField) return;

        const control = this.control.control;
        const isRequired = !!control.validator?.({} as AbstractControl)?.['required'];
        const hasValue = !!control.value?.toString().trim();

        if (!isRequired && !hasValue) {
            this.renderer.addClass(this.matFormField, 'yellow-border');
        } else {
            this.renderer.removeClass(this.matFormField, 'yellow-border');
        }
    }

    /**
     * Optimized function to find the closest `.mat-mdc-form-field`
     */
    private findClosestMatFormField(): HTMLElement | null {
        let parent = this.el.nativeElement;
        while (parent && !parent.classList.contains('mat-mdc-form-field')) {
            parent = parent.parentElement;
        }
        return parent;
    }
}
